import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { CreateDepartmentRequest } from '../types/Company';
import styles from './DepartmentModal.module.css';

type DepartmentOption = {
  id: string;
  label: string;
};

type DepartmentModalProps = {
  isOpen: boolean;
  isEditing: boolean;
  form: CreateDepartmentRequest;
  parentOptions: DepartmentOption[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onChange: (form: CreateDepartmentRequest) => void;
  onSave: () => void;
};

export const DepartmentModal: React.FC<DepartmentModalProps> = ({
  isOpen,
  isEditing,
  form,
  parentOptions,
  saving,
  error,
  onClose,
  onChange,
  onSave,
}) => {
  const { t } = useTranslation(['companies', 'admin', 'common']);

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) {
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen, onClose, saving]);

  if (!isOpen) {
    return null;
  }

  const title = isEditing ? t('editDepartment', { ns: 'companies' }) : t('createDepartment', { ns: 'companies' });
  const saveLabel = saving
    ? t('savingEllipsis', { ns: 'admin' })
    : isEditing
      ? t('updateDepartment', { ns: 'companies' })
      : t('createDepartment', { ns: 'companies' });

  return createPortal(
    <div
      className={styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="department-modal-title"
      onClick={() => {
        if (!saving) {
          onClose();
        }
      }}
    >
      <div className={styles.modalCard} onClick={event => event.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3 id="department-modal-title" className={styles.modalTitle}>{title}</h3>
          <button
            className={styles.modalCloseBtn}
            type="button"
            onClick={onClose}
            aria-label={t('close', { ns: 'common' })}
            disabled={saving}
          >
            ×
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formGrid}>
            <label>
              {t('departmentName', { ns: 'companies' })}
              <input
                className={`text-input ${styles.fieldInput}`}
                value={form.name || ''}
                onChange={event => onChange({ ...form, name: event.target.value })}
                autoFocus
              />
            </label>
            <label>
              {t('parentDepartment', { ns: 'companies' })}
              <select
                className={`text-input ${styles.fieldInput}`}
                value={form.parentDepartmentId || ''}
                onChange={event => onChange({ ...form, parentDepartmentId: event.target.value })}
              >
                <option value="">{t('rootDepartment', { ns: 'companies' })}</option>
                {parentOptions.map(option => (
                  <option key={option.id} value={option.id}>{option.label}</option>
                ))}
              </select>
            </label>
            <label>
              {t('status', { ns: 'admin' })}
              <select
                className={`text-input ${styles.fieldInput}`}
                value={form.isActive ? 'active' : 'inactive'}
                onChange={event => onChange({ ...form, isActive: event.target.value === 'active' })}
              >
                <option value="active">{t('active', { ns: 'admin' })}</option>
                <option value="inactive">{t('inactive', { ns: 'admin' })}</option>
              </select>
            </label>
          </div>
        </div>

        <div className={styles.modalFooter}>
          {error ? <div className="error">{error}</div> : null}
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
            {isEditing ? t('cancelEdit', { ns: 'companies' }) : t('cancel', { ns: 'common' })}
          </button>
          <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saveLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default DepartmentModal;
